// src/pages/finance-portal/payment-links/PaymentLinks.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tooltip,
    InputAdornment, Autocomplete, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Checkbox,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    EXPIRED: { bg: '#f1f5f9', color: '#475569' },
    USED: { bg: '#dbeafe', color: '#1e40af' },
    CANCELLED: { bg: '#fecaca', color: '#991b1b' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function truncateToken(token) {
    if (!token) return '\u2014';
    if (token.length <= 16) return token;
    return token.substring(0, 8) + '...' + token.substring(token.length - 8);
}

export default function PaymentLinks() {
    const [links, setLinks] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [search, setSearch] = useState('');

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({
        invoice_id: null,
        customer_id: null,
        amount: '',
        description: '',
        allow_partial: false,
        allow_tip: false,
        expires_at: '',
    });

    // Invoice autocomplete
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const invoiceTimer = useRef(null);

    // Customer autocomplete
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerLoading, setCustomerLoading] = useState(false);
    const customerTimer = useRef(null);

    // Generated link dialog
    const [generatedLink, setGeneratedLink] = useState(null);
    const [copied, setCopied] = useState(false);

    // Revoke dialog
    const [revokeDialog, setRevokeDialog] = useState({ open: false, link: null });
    const [revoking, setRevoking] = useState(false);

    const fetchLinks = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/payment-links', { params });
            if (res.data.success) {
                setLinks(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payment links');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, search]);

    useEffect(() => { fetchLinks(); }, [fetchLinks]);

    // Invoice search
    useEffect(() => {
        if (invoiceTimer.current) clearTimeout(invoiceTimer.current);
        if (!createOpen) { setInvoiceOptions([]); return; }
        setInvoiceLoading(true);
        invoiceTimer.current = setTimeout(async () => {
            try {
                const params = { per_page: 20 };
                if (invoiceSearch) params.search = invoiceSearch;
                const res = await axiosInstance.get('/api/finance/invoices', { params });
                if (res.data.success) {
                    setInvoiceOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch invoices', err);
            } finally {
                setInvoiceLoading(false);
            }
        }, 300);
        return () => { if (invoiceTimer.current) clearTimeout(invoiceTimer.current); };
    }, [createOpen, invoiceSearch]);

    // Customer search
    useEffect(() => {
        if (customerTimer.current) clearTimeout(customerTimer.current);
        if (!createOpen) { setCustomerOptions([]); return; }
        setCustomerLoading(true);
        customerTimer.current = setTimeout(async () => {
            try {
                const params = { per_page: 20 };
                if (customerSearch) params.search = customerSearch;
                const res = await axiosInstance.get('/api/finance/customers', { params });
                if (res.data.success) {
                    setCustomerOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch customers', err);
            } finally {
                setCustomerLoading(false);
            }
        }, 300);
        return () => { if (customerTimer.current) clearTimeout(customerTimer.current); };
    }, [createOpen, customerSearch]);

    const openCreateDialog = () => {
        setCreateForm({
            invoice_id: null,
            customer_id: null,
            amount: '',
            description: '',
            allow_partial: false,
            allow_tip: false,
            expires_at: '',
        });
        setInvoiceSearch('');
        setCustomerSearch('');
        setCreateOpen(true);
    };

    const handleCreate = async () => {
        setCreating(true);
        setError(null);
        try {
            const payload = {
                description: createForm.description,
                allow_partial: createForm.allow_partial,
                allow_tip: createForm.allow_tip,
            };
            if (createForm.invoice_id) payload.invoice_id = createForm.invoice_id.id;
            if (createForm.customer_id) payload.customer_id = createForm.customer_id.id;
            if (createForm.amount) payload.amount = parseFloat(createForm.amount);
            if (createForm.expires_at) payload.expires_at = createForm.expires_at;

            const res = await axiosInstance.post('/api/finance/payment-links', payload);
            if (res.data.success) {
                setCreateOpen(false);
                setGeneratedLink(res.data.data);
                setCopied(false);
                fetchLinks();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create payment link');
        } finally {
            setCreating(false);
        }
    };

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setSuccess('Payment link copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            setError('Failed to copy link');
        });
    };

    const handleCopyFromTable = (link) => {
        const url = link.url || link.payment_url || `${window.location.origin}/pay/${link.token}`;
        navigator.clipboard.writeText(url).then(() => {
            setSuccess('Payment link copied to clipboard');
        }).catch(() => {
            setError('Failed to copy link');
        });
    };

    const handleRevoke = async () => {
        setRevoking(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/payment-links/${revokeDialog.link.id}/revoke`);
            setRevokeDialog({ open: false, link: null });
            setSuccess('Payment link revoked successfully');
            fetchLinks();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to revoke payment link');
        } finally {
            setRevoking(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <LinkIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Payment Links</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Create and manage shareable payment links</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Create Payment Link
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search payment links..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Link Token', 'Invoice #', 'Customer', 'Amount', 'Partial?', 'Tip?', 'Expires', 'Views', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : links.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6 }}>
                                            <LinkIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No payment links found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={openCreateDialog}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Create your first payment link
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : links.map(link => {
                                    const statusStyle = STATUS_COLORS[link.status] || {};
                                    const customerName = link.customer_name || link.customer?.company_name || link.customer?.name || '\u2014';
                                    const invoiceNumber = link.invoice_number || link.invoice?.invoice_number || '\u2014';

                                    return (
                                        <TableRow key={link.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                <Tooltip title={link.token || ''}>
                                                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{truncateToken(link.token)}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {invoiceNumber}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {link.amount ? formatCurrency(link.amount) : '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#475569' }}>
                                                {link.allow_partial ? (
                                                    <Chip label="Yes" size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: '#dbeafe', color: '#1e40af' }} />
                                                ) : (
                                                    <Chip label="No" size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: '#f1f5f9', color: '#64748b' }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#475569' }}>
                                                {link.allow_tip ? (
                                                    <Chip label="Yes" size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: '#dbeafe', color: '#1e40af' }} />
                                                ) : (
                                                    <Chip label="No" size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: '#f1f5f9', color: '#64748b' }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(link.expires_at)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textAlign: 'center' }}>
                                                {link.view_count != null ? link.view_count : 0}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={link.status}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Copy Link">
                                                        <IconButton size="small" onClick={() => handleCopyFromTable(link)} sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}>
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {link.status === 'ACTIVE' && (
                                                        <Tooltip title="Revoke">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setRevokeDialog({ open: true, link })}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                            >
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Create Payment Link Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon sx={{ color: ACCENT }} />
                    Create Payment Link
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <Autocomplete
                            size="small"
                            options={invoiceOptions}
                            getOptionLabel={opt => opt ? `${opt.invoice_number || 'INV-???'} - ${formatCurrency(opt.total)}` : ''}
                            value={createForm.invoice_id}
                            onChange={(_, val) => setCreateForm(prev => ({ ...prev, invoice_id: val }))}
                            onInputChange={(_, val) => setInvoiceSearch(val)}
                            loading={invoiceLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Invoice (optional)"
                                    placeholder="Search invoices..."
                                    InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px' },
                                        endAdornment: (
                                            <>
                                                {invoiceLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="No invoices found"
                        />

                        <Autocomplete
                            size="small"
                            options={customerOptions}
                            getOptionLabel={opt => opt ? (opt.company_name || opt.name || opt.email || '') : ''}
                            value={createForm.customer_id}
                            onChange={(_, val) => setCreateForm(prev => ({ ...prev, customer_id: val }))}
                            onInputChange={(_, val) => setCustomerSearch(val)}
                            loading={customerLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Customer (optional)"
                                    placeholder="Search customers..."
                                    InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px' },
                                        endAdornment: (
                                            <>
                                                {customerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="No customers found"
                        />

                        <TextField
                            size="small"
                            label="Amount (optional)"
                            type="number"
                            value={createForm.amount}
                            onChange={e => setCreateForm(prev => ({ ...prev, amount: e.target.value }))}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                sx: { fontFamily, borderRadius: '10px' },
                            }}
                            helperText="Leave blank to let the payer enter the amount"
                            FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.75rem' } }}
                        />

                        <TextField
                            size="small"
                            label="Description"
                            multiline
                            rows={2}
                            value={createForm.description}
                            onChange={e => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={createForm.allow_partial}
                                        onChange={e => setCreateForm(prev => ({ ...prev, allow_partial: e.target.checked }))}
                                        sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Allow partial payments</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={createForm.allow_tip}
                                        onChange={e => setCreateForm(prev => ({ ...prev, allow_tip: e.target.checked }))}
                                        sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Allow tip</Typography>}
                            />
                        </Box>

                        <TextField
                            size="small"
                            label="Expiration Date"
                            type="date"
                            value={createForm.expires_at}
                            onChange={e => setCreateForm(prev => ({ ...prev, expires_at: e.target.value }))}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            helperText="Leave blank for no expiration"
                            FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.75rem' } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={creating || !createForm.description}
                        startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {creating ? 'Creating...' : 'Create Link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generated Link Dialog */}
            <Dialog open={!!generatedLink} onClose={() => setGeneratedLink(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: ACCENT }} />
                    Payment Link Created
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 2, fontSize: '0.9rem' }}>
                        Your payment link has been created successfully. Share this link with your customer to collect payment.
                    </Typography>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                    }}>
                        <Typography sx={{
                            fontFamily: 'monospace', fontSize: '0.85rem', color: '#1e293b',
                            flex: 1, wordBreak: 'break-all',
                        }}>
                            {generatedLink?.url || generatedLink?.payment_url || `${window.location.origin}/pay/${generatedLink?.token}`}
                        </Typography>
                        <Tooltip title={copied ? 'Copied!' : 'Copy Link'}>
                            <IconButton
                                onClick={() => handleCopyLink(generatedLink?.url || generatedLink?.payment_url || `${window.location.origin}/pay/${generatedLink?.token}`)}
                                sx={{ color: copied ? ACCENT : '#94a3b8', '&:hover': { color: ACCENT } }}
                            >
                                {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {generatedLink?.amount && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Amount</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1rem', color: ACCENT, fontWeight: 700 }}>{formatCurrency(generatedLink.amount)}</Typography>
                            </Box>
                            {generatedLink.expires_at && (
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Expires</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1rem', color: '#1e293b', fontWeight: 500 }}>{formatDate(generatedLink.expires_at)}</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setGeneratedLink(null)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={() => handleCopyLink(generatedLink?.url || generatedLink?.payment_url || `${window.location.origin}/pay/${generatedLink?.token}`)}
                        startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {copied ? 'Copied!' : 'Copy Link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirmation Dialog */}
            <Dialog open={revokeDialog.open} onClose={() => setRevokeDialog({ open: false, link: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Revoke Payment Link?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently deactivate this payment link. Customers will no longer be able to use it to make payments. This action cannot be undone.
                    </Typography>
                    {revokeDialog.link?.token && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#64748b' }}>
                                Token: {truncateToken(revokeDialog.link.token)}
                            </Typography>
                            {revokeDialog.link.amount && (
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 600, mt: 0.5 }}>
                                    Amount: {formatCurrency(revokeDialog.link.amount)}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRevokeDialog({ open: false, link: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRevoke}
                        disabled={revoking}
                        startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {revoking ? 'Revoking...' : 'Revoke Link'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
